/**
 * ReservationQueueList — the FCFS waiting line attached to ONE reservation.
 *
 * Props-only component. No data fetching, no routing. Rendered (expanded) beneath
 * a reservation row on the unit reservation panel.
 *
 * Domain model:
 *   - The queue is separate from reservations. This list shows the people waiting
 *     behind a single held reservation, in strict first-come-first-served order.
 *   - A waiting entry whose date range overlaps several reservations appears under
 *     each of them; `spansMultiple` flags those so the operator knows the entry is
 *     also waiting on another period.
 *   - Order is fixed (FCFS) — there is no manual reordering.
 *
 * Row hierarchy (CU-86cb3x6y6 follow-up): the PRIMARY line is
 * `clientName ?? offerName ?? labels.noOfferLabel` — not `userName`. An admin
 * scanning a waitlist is deciding "which client is waiting, on which offer",
 * not "which internal person clicked join"; `userName` (the submitter — often
 * the salesperson/rezerwujący, sometimes the client acting directly) is
 * attribution and renders small, on its own line, prefixed by
 * `labels.submittedByLabel`. The offer name is a clickable link
 * (`offerHref`, same convention as `UnitReservationRow.offerHref` in
 * `unit-reservations-tab.tsx`) so the operator can open the linked offer
 * directly from the queue, matching every other offer reference on this
 * screen instead of being dead text.
 *
 * Actions are icon-only with hover tooltips (CU-86cb3x6y6 follow-up: the
 * original "crown" glyph read as ambiguous — VIP/favourite — to non-technical
 * testers instead of "transfer the reservation"; swapped for a bidirectional
 * swap-arrows icon, which testing showed reads clearly on its own — visible
 * always-on text labels were tried and dropped again as too much noise in an
 * already-dense row), gated by TWO INDEPENDENT permission flags (promoting a
 * queue entry force-releases someone else's active reservation, which is a
 * strictly stronger action than removing a queue entry, so it requires a
 * strictly stronger permission):
 *   - canManageQueue (reservation.manage_waitlist): may remove any entry.
 *   - canDisplacePromote (reservation.manage_waitlist AND reservation.force_release):
 *     may additionally displace-and-promote (take over the reservation). Never
 *     shown on its own without canManageQueue also being true in practice, but
 *     the two are intentionally separate props so a caller holding only
 *     manage_waitlist never renders the promote affordance.
 *   - Non-admin viewing their OWN entry (neither flag set): leave the queue
 *     (self-service).
 * Order is fixed (first-come, first-served) — there is NO manual reordering.
 *
 * SECURITY: the permission gates here (canManageQueue, canDisplacePromote,
 * ownership for leave) are presentational only. The server MUST independently
 * re-verify role, ownership, AND reservation.force_release on the promote
 * mutation — client-provided identity is untrusted.
 */

import { ArrowRightLeft, UserMinus, LogOut } from 'lucide-react';
import { Button } from '@ooh/ui/components/button';
import { cn } from '@ooh/ui/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@ooh/ui/components/tooltip';
import { QueueEntryIdentity } from './queue-entry-parts';

/** The mutations a caller can perform on a queue entry. */
export type QueueAction = 'leave' | 'remove' | 'promote';

export interface QueueEntryView {
  id: number;
  /** 1-based position within THIS reservation's queue. */
  position: number;
  /** The person who submitted this queue entry (often the salesperson/rezerwujący, sometimes the client themselves). Attribution only — see the component doc for why this is NOT the row's primary display field. */
  userId: number;
  userName: string;
  offerName: string | null;
  /** URL to the offer workspace (pre-computed by caller, same convention as `UnitReservationRow.offerHref`). Null when the entry has no linked offer. CU-86cb3x6y6 follow-up: without this the offer name was dead text with no way to open it, unlike every other offer reference in this screen. */
  offerHref?: string | null;
  /**
   * Client tied to the queue entry's offer, when it has one. THE PRIMARY
   * identity for this row (see component doc) — an admin scanning the queue
   * cares who the waiting client is, not who internally clicked "join".
   * Optional so existing callers (e.g. the production /media-units/:id route
   * mapper, not yet updated in this change) keep typechecking; defaults to
   * null when omitted, in which case the row falls back to `offerName`, then
   * `userName`.
   */
  clientName?: string | null;
  /** Pre-formatted by the caller. */
  dateFrom: string;
  dateTo: string;
  /** True when the entry's window overlaps more than one reservation on the unit. */
  spansMultiple: boolean;
  /**
   * Entry subtype from QueueEntry.subtype ('SOFT' | 'HARD').
   * HARD entries render with text-destructive (red) treatment.
   */
  subtype?: 'SOFT' | 'HARD';
  /**
   * Salesperson (handlowiec) owning the commercial relationship behind this
   * entry — distinct from `userName` (whoever submitted the join) and from the
   * held reservation's rezerwujący. CU-86cb7ctfw: the cross-unit reservations list
   * needs it to answer "whose deal is waiting here" — there you cannot see the
   * unit's other terms for context; omitted on the single-unit panel, where
   * the extra line would be noise.
   */
  salespersonName?: string | null;
  /** Pre-formatted date the entry joined the line (drives FCFS order). Optional — only the overview surfaces it. */
  joinedAtLabel?: string | null;
  /** Pre-formatted expected promotion date (when the blocking reservation frees the term). Optional — only the overview surfaces it. */
  promotionEtaLabel?: string | null;
  /**
   * Client's brand snapshot at join time (CU-86cb7qgc3), resolved server-side
   * from the entry's own persisted `client_id`/`brand` columns — NOT a live
   * join through the linked offer, so it never changes if that offer is
   * edited later. Renders as an additional optional meta line alongside
   * `salespersonName`/`joinedAtLabel`/`promotionEtaLabel`, following the same
   * "renders only when both the field and its label are supplied" pattern.
   */
  brand?: string | null;
}

export interface ReservationQueueListLabels {
  /** Column-ish header above the list, e.g. "Kolejka oczekujących". */
  title: string;
  /**
   * Plain-language explainer shown under the title, e.g. "Kolejność według
   * czasu zgłoszenia" — replaces the raw "(FCFS)" engineering acronym that
   * non-technical testers could not parse.
   */
  subtitle: string;
  noOfferLabel: string;
  positionLabel: (position: number) => string;
  spansMultipleLabel: string;
  /** Small badge on the current user's own row, e.g. "Ty". */
  ownBadgeLabel: string;
  /** Prefix for the small attribution line, e.g. "Zgłosił:" — precedes `userName`. */
  submittedByLabel: string;
  leaveLabel: string;
  removeLabel: string;
  promoteLabel: string;
  /**
   * Prefix for the optional salesperson line, e.g. "Handlowiec:". Only used
   * when the caller supplies `QueueEntryView.salespersonName` (CU-86cb7ctfw).
   */
  salespersonLabel?: string;
  /** Prefix for the optional join-date line, e.g. "Dołączył:". Only used when `joinedAtLabel` is supplied. */
  joinedAtLabel?: string;
  /** Prefix for the optional promotion-ETA line, e.g. "Przewidywany awans:". Only used when `promotionEtaLabel` is supplied. */
  promotionEtaLabel?: string;
  /** Prefix for the optional brand line, e.g. "Marka:". Only used when `QueueEntryView.brand` is supplied (CU-86cb7qgc3). */
  brandLabel?: string;
}

export interface ReservationQueueListPermissions {
  /** Holder of reservation.manage_waitlist: may remove any entry. */
  canManageQueue: boolean;
  /**
   * Holder of BOTH reservation.manage_waitlist AND reservation.force_release:
   * may additionally displace-and-promote (force-release the current holder's
   * active reservation and hand this entry the term). CU-86cb3x6y6: this MUST
   * stay independent of canManageQueue — manage_waitlist alone only grants
   * removal, never displacement.
   */
  canDisplacePromote: boolean;
}

export interface ReservationQueueListProps {
  reservationId: number;
  entries: QueueEntryView[];
  currentUserId: number;
  permissions: ReservationQueueListPermissions;
  labels: ReservationQueueListLabels;
  onQueueAction: (action: QueueAction, entryId: number) => void;
}

/** Icon-only action button group for one queue row — extracted to keep the row's complexity within budget. */
function QueueEntryActions({
  entry,
  isOwn,
  permissions,
  labels,
  onQueueAction,
}: {
  entry: QueueEntryView;
  isOwn: boolean;
  permissions: ReservationQueueListPermissions;
  labels: ReservationQueueListLabels;
  onQueueAction: (action: QueueAction, entryId: number) => void;
}) {
  return (
    <TooltipProvider>
      <div className="flex shrink-0 items-center gap-0.5">
        {permissions.canDisplacePromote ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="icon-sm"
                variant="ghost"
                data-testid={`queue-promote-${entry.id}`}
                aria-label={labels.promoteLabel}
                onClick={() => onQueueAction('promote', entry.id)}
              >
                <ArrowRightLeft className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{labels.promoteLabel}</TooltipContent>
          </Tooltip>
        ) : null}
        {permissions.canManageQueue ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="icon-sm"
                variant="ghost"
                className="text-destructive hover:text-destructive"
                data-testid={`queue-remove-${entry.id}`}
                aria-label={labels.removeLabel}
                onClick={() => onQueueAction('remove', entry.id)}
              >
                <UserMinus className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{labels.removeLabel}</TooltipContent>
          </Tooltip>
        ) : isOwn ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="icon-sm"
                variant="ghost"
                className="text-destructive hover:text-destructive"
                data-testid={`queue-leave-${entry.id}`}
                aria-label={labels.leaveLabel}
                onClick={() => onQueueAction('leave', entry.id)}
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{labels.leaveLabel}</TooltipContent>
          </Tooltip>
        ) : null}
      </div>
    </TooltipProvider>
  );
}

/** One waiting-line row — extracted from the list's map() to keep cyclomatic complexity within budget. */
function QueueEntryRow({
  entry,
  isOwn,
  permissions,
  labels,
  onQueueAction,
}: {
  entry: QueueEntryView;
  isOwn: boolean;
  permissions: ReservationQueueListPermissions;
  labels: ReservationQueueListLabels;
  onQueueAction: (action: QueueAction, entryId: number) => void;
}) {
  return (
    <li
      data-testid={`queue-entry-${entry.id}`}
      data-own={isOwn ? 'true' : undefined}
      data-subtype={entry.subtype ?? undefined}
      className={cn(
        'flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded border-y border-r border-border/50 bg-background px-2.5 py-1.5',
        isOwn ? 'border-l-4 border-l-primary' : 'border-l border-l-border/50',
      )}
    >
      <span
        className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-muted text-2xs font-semibold tabular-nums"
        aria-label={labels.positionLabel(entry.position)}
      >
        {entry.position}
      </span>
      <QueueEntryIdentity entry={entry} isOwn={isOwn} labels={labels} />
      {entry.spansMultiple ? (
        <span
          data-testid={`queue-entry-spans-${entry.id}`}
          className="shrink-0 rounded-full border border-border bg-muted px-1.5 py-0.5 text-2xs text-muted-foreground"
        >
          {labels.spansMultipleLabel}
        </span>
      ) : null}
      <QueueEntryActions
        entry={entry}
        isOwn={isOwn}
        permissions={permissions}
        labels={labels}
        onQueueAction={onQueueAction}
      />
    </li>
  );
}

export function ReservationQueueList({
  reservationId,
  entries,
  currentUserId,
  permissions,
  labels,
  onQueueAction,
}: ReservationQueueListProps) {
  if (entries.length === 0) return null;

  return (
    <div
      data-testid={`reservation-queue-${reservationId}`}
      className="rounded-md border border-border/60 bg-muted/20 px-3 py-2.5"
    >
      <div className="mb-2">
        <p className="text-2xs font-semibold uppercase tracking-wide text-muted-foreground">
          {labels.title}
        </p>
        <p className="text-2xs text-muted-foreground/80">{labels.subtitle}</p>
      </div>
      <ol className="space-y-1.5">
        {entries.map((entry) => (
          <QueueEntryRow
            key={entry.id}
            entry={entry}
            isOwn={entry.userId === currentUserId}
            permissions={permissions}
            labels={labels}
            onQueueAction={onQueueAction}
          />
        ))}
      </ol>
    </div>
  );
}
